// src/pages/AccountPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import authApi from '../services/authApi';
import { formatPrice } from '../services/shopApi';

export default function AccountPage() {
  const { consumer, logout, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview'); // overview | orders | profile | security
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPagination, setOrdersPagination] = useState(null);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) navigate('/');
  }, [isLoggedIn, navigate]);

  // Load orders when tab switches
  useEffect(() => {
    if (tab === 'orders' && consumer?.email) {
      loadOrders(1);
    }
  }, [tab, consumer]);

  const loadOrders = async (page = 1) => {
    setOrdersLoading(true);
    try {
      const r = await authApi.getOrderHistory(consumer.email, page);
      if (r.success) {
        setOrders(r.data.orders || []);
        setOrdersPagination(r.data.pagination);
        setOrdersPage(page);
      }
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!consumer) return null;

  const firstName = consumer.profile?.firstName || consumer.firstName || consumer.email?.split('@')[0] || 'User';
  const lastName = consumer.profile?.lastName || consumer.lastName || '';
  const avatarUrl = consumer.profile?.avatarUrl;
  const tier = consumer.loyalty?.tier || 'bronze';
  const points = consumer.loyalty?.points || 0;
  const totalScans = consumer.loyalty?.totalScans || 0;

  const tierColors = {
    bronze: { bg: 'rgba(180,83,9,0.15)', color: '#F59E0B', border: 'rgba(180,83,9,0.3)' },
    silver: { bg: 'rgba(156,163,175,0.15)', color: '#9CA3AF', border: 'rgba(156,163,175,0.3)' },
    gold: { bg: 'rgba(234,179,8,0.15)', color: '#EAB308', border: 'rgba(234,179,8,0.3)' },
    platinum: { bg: 'rgba(0,229,204,0.15)', color: '#00E5CC', border: 'rgba(0,229,204,0.3)' },
  };
  const tc = tierColors[tier] || tierColors.bronze;

  const statusColors = {
    pending: { bg: 'rgba(234,179,8,0.1)', color: '#EAB308' },
    confirmed: { bg: 'rgba(59,130,246,0.1)', color: '#3B82F6' },
    processing: { bg: 'rgba(139,92,246,0.1)', color: '#8B5CF6' },
    shipped: { bg: 'rgba(0,229,204,0.1)', color: '#00E5CC' },
    delivered: { bg: 'rgba(34,197,94,0.1)', color: '#22C55E' },
    cancelled: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
  };

  const tabs = [
    { id: 'overview', label: '📊 Overview', icon: '📊' },
    { id: 'orders', label: '📦 Orders', icon: '📦' },
    { id: 'profile', label: '👤 Profile', icon: '👤' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#030A14', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2140 60%, #112240 100%)',
        borderBottom: '1px solid rgba(0,229,204,0.08)',
        padding: '40px 40px 0',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Breadcrumb */}
          <div style={{ fontSize: 12, color: 'rgba(224,240,240,0.3)', marginBottom: 24 }}>
            <Link to="/" style={{ color: 'rgba(224,240,240,0.3)', textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 8px' }}>›</span>
            <span style={{ color: '#00E5CC' }}>My Account</span>
          </div>

          {/* Profile header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 32 }}>
            {/* Avatar */}
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: avatarUrl ? 'transparent' : 'linear-gradient(135deg, #00E5CC, #22D97F)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, fontWeight: 700, color: '#030A14',
              border: '2px solid rgba(0,229,204,0.3)',
              overflow: 'hidden', flexShrink: 0,
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : firstName[0]?.toUpperCase()
              }
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 style={{ color: '#E0F0F0', fontSize: 24, fontWeight: 700, margin: 0 }}>
                  {firstName} {lastName}
                </h1>
                <span style={{
                  padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '1px',
                  background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                }}>
                  {tier}
                </span>
              </div>
              <div style={{ color: 'rgba(224,240,240,0.4)', fontSize: 14, marginTop: 4 }}>
                {consumer.email}
              </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: 24, flexShrink: 0 }}>
              {[
                { label: 'Points', value: points.toLocaleString(), color: '#00E5CC' },
                { label: 'Scans', value: totalScans, color: '#22D97F' },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'rgba(224,240,240,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,229,204,0.06)' }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '12px 20px', border: 'none', background: 'transparent',
                color: tab === t.id ? '#00E5CC' : 'rgba(224,240,240,0.4)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                borderBottom: tab === t.id ? '2px solid #00E5CC' : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.2s',
              }}>
                {t.label}
              </button>
            ))}
            <button onClick={handleLogout} style={{
              marginLeft: 'auto', padding: '12px 20px', border: 'none', background: 'transparent',
              color: 'rgba(239,68,68,0.6)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              transition: 'color 0.2s',
            }} onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
               onMouseLeave={e => e.currentTarget.style.color = 'rgba(239,68,68,0.6)'}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1100, margin: '32px auto', padding: '0 40px' }}>

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
              {[
                { label: 'Loyalty Points', value: points.toLocaleString(), icon: '⭐', color: '#00E5CC', sub: `${tier} tier` },
                { label: 'Products Scanned', value: totalScans, icon: '🏷️', color: '#22D97F', sub: 'verified authentic' },
                { label: 'Member Since', value: new Date(consumer.createdAt || Date.now()).getFullYear(), icon: '📅', color: '#7C3AED', sub: 'JepTags member' },
              ].map(s => (
                <div key={s.label} style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 16, padding: '24px 20px',
                }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 13, color: '#E0F0F0', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 12, color: 'rgba(224,240,240,0.35)', marginTop: 2 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ color: '#E0F0F0', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Quick Actions</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {[
                  { icon: '📦', label: 'View Orders', action: () => setTab('orders') },
                  { icon: '🛍️', label: 'Continue Shopping', action: () => navigate('/products') },
                  { icon: '👤', label: 'Edit Profile', action: () => setTab('profile') },
                  { icon: '🏷️', label: 'Browse Brands', action: () => navigate('/brands') },
                ].map(a => (
                  <button key={a.label} onClick={a.action} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 12, padding: '16px 12px', cursor: 'pointer',
                    color: '#E0F0F0', textAlign: 'center', transition: 'all 0.2s',
                  }} onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(0,229,204,0.2)'; e.currentTarget.style.background = 'rgba(0,229,204,0.05)'; }}
                     onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}>
                    <div style={{ fontSize: 24, marginBottom: 8 }}>{a.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(224,240,240,0.7)' }}>{a.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Loyalty info */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(0,229,204,0.05), rgba(34,217,127,0.05))',
              border: '1px solid rgba(0,229,204,0.12)', borderRadius: 16, padding: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#E0F0F0', marginBottom: 4 }}>
                    🎯 JepTags Loyalty Program
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(224,240,240,0.5)' }}>
                    Scan authentic products to earn points and unlock exclusive rewards
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  {['Bronze', 'Silver', 'Gold', 'Platinum'].map((t, i) => (
                    <div key={t} style={{
                      textAlign: 'center', opacity: ['bronze','silver','gold','platinum'].indexOf(tier) >= i ? 1 : 0.3,
                    }}>
                      <div style={{ fontSize: 18 }}>{'🥉🥈🥇💎'[i]}</div>
                      <div style={{ fontSize: 10, color: 'rgba(224,240,240,0.5)', marginTop: 2 }}>{t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ORDERS ── */}
        {tab === 'orders' && (
          <div>
            <h2 style={{ color: '#E0F0F0', fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Order History</h2>

            {ordersLoading ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'rgba(224,240,240,0.3)' }}>
                Loading orders...
              </div>
            ) : orders.length === 0 ? (
              <div style={{
                background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 16, padding: '60px 40px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
                <div style={{ color: '#E0F0F0', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No orders yet</div>
                <div style={{ color: 'rgba(224,240,240,0.4)', fontSize: 14, marginBottom: 24 }}>
                  Start shopping to see your order history here
                </div>
                <Link to="/products" style={{
                  display: 'inline-block', padding: '12px 24px',
                  background: 'linear-gradient(135deg, #00E5CC, #22D97F)',
                  color: '#030A14', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: 14,
                }}>
                  Shop Now →
                </Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {orders.map(order => {
                  const sc = statusColors[order.status] || statusColors.pending;
                  return (
                    <div key={order._id || order.orderId} style={{
                      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 16, padding: '20px 24px',
                      transition: 'border-color 0.2s',
                    }} onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,229,204,0.15)'}
                       onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                            <span style={{ color: '#E0F0F0', fontWeight: 700, fontSize: 15 }}>
                              {order.orderId}
                            </span>
                            <span style={{
                              padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.5px',
                              background: sc.bg, color: sc.color,
                            }}>
                              {order.status}
                            </span>
                          </div>
                          <div style={{ color: 'rgba(224,240,240,0.4)', fontSize: 12 }}>
                            {new Date(order.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            {' · '}{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#00E5CC', fontWeight: 700, fontSize: 18 }}>
                            {formatPrice(order.total, order.currency)}
                          </div>
                          <div style={{ color: 'rgba(224,240,240,0.35)', fontSize: 11, marginTop: 2 }}>
                            {order.payment?.method?.toUpperCase() || 'COD'}
                          </div>
                        </div>
                      </div>

                      {/* Items preview */}
                      {order.items?.length > 0 && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 10 }}>
                          {order.items.slice(0, 3).map((item, i) => (
                            <div key={i} style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '6px 10px',
                            }}>
                              {item.image && <img src={item.image} alt={item.name} style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 6 }} />}
                              <span style={{ fontSize: 12, color: 'rgba(224,240,240,0.6)' }}>{item.name}</span>
                              <span style={{ fontSize: 11, color: 'rgba(224,240,240,0.3)' }}>×{item.quantity}</span>
                            </div>
                          ))}
                          {order.items.length > 3 && (
                            <div style={{ fontSize: 12, color: 'rgba(224,240,240,0.3)', display: 'flex', alignItems: 'center' }}>
                              +{order.items.length - 3} more
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Pagination */}
                {ordersPagination && ordersPagination.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                    {Array.from({ length: ordersPagination.pages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => loadOrders(p)} style={{
                        width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer',
                        background: p === ordersPage ? 'rgba(0,229,204,0.15)' : 'rgba(255,255,255,0.04)',
                        color: p === ordersPage ? '#00E5CC' : 'rgba(224,240,240,0.4)',
                        fontWeight: p === ordersPage ? 700 : 400, fontSize: 13,
                        boxShadow: p === ordersPage ? 'inset 0 0 0 1px rgba(0,229,204,0.3)' : 'none',
                      }}>{p}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PROFILE ── */}
        {tab === 'profile' && (
          <div style={{ maxWidth: 520 }}>
            <h2 style={{ color: '#E0F0F0', fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Profile Information</h2>
            <div style={{
              background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 16, padding: 24,
            }}>
              {[
                { label: 'First Name', value: consumer.profile?.firstName || consumer.firstName || '—' },
                { label: 'Last Name', value: consumer.profile?.lastName || consumer.lastName || '—' },
                { label: 'Email', value: consumer.email },
                { label: 'Phone', value: consumer.phone || consumer.profile?.phone || '—' },
                { label: 'Member ID', value: consumer.consumerId || '—' },
                { label: 'Login Method', value: consumer.loginType || 'email' },
                { label: 'Email Verified', value: consumer.isEmailVerified ? '✅ Verified' : '⚠️ Not verified' },
              ].map(f => (
                <div key={f.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <span style={{ fontSize: 13, color: 'rgba(224,240,240,0.4)', fontWeight: 500 }}>{f.label}</span>
                  <span style={{ fontSize: 13, color: '#E0F0F0', fontWeight: 600 }}>{f.value}</span>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: 16, padding: '14px 16px',
              background: 'rgba(0,229,204,0.05)', border: '1px solid rgba(0,229,204,0.12)',
              borderRadius: 12, fontSize: 12, color: 'rgba(224,240,240,0.4)',
            }}>
              💡 To update your profile, use the JepTags mobile app or contact support.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
